import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import styled, { css } from 'styled-components';
import PT from 'prop-types';
import { t as theme } from '../theme/themes/default-BrkF3upi.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { u as useIsMobile } from '../theme/MobileThemeProvider/useIsMobile-B-VP6bHU.js';
import { m as mobileSpacing } from '../theme/base/spacing-zNVNrnl_.js';
import { g as getPadding } from '../LocalModules/storm-ui-utils/dist/esm/getPadding-Bvzhcf_u-BhHYy5ob.js';
import { D as DEVICE_TYPES } from '../LocalModules/storm-ui-utils/dist/esm/deviceTypes-wW4MAd2m-wW4MAd2m.js';

const getContainerContentMaxWidth = (size = 'fluid') => {
  if (theme && theme.pageLayout && typeof theme.pageLayout.section.contentMaxWidth[size] !== 'undefined') {
    return theme.pageLayout.section.contentMaxWidth[size];
  }
  return '100%';
};
const getBgColor = (color = 'default') => {
  if (theme && theme.pageLayout && typeof theme.pageLayout.section.background[color] !== 'undefined') {
    return theme.pageLayout.section.background[color];
  }
  return '#FFFFFF';
};
const applyPaddingInlineDefault = /*#__PURE__*/css(["padding-inline-start:", ";padding-inline-end:", ";"], ({
  $isMobile,
  theme
}) => $isMobile ? mobileSpacing.base : theme.spacing.large, ({
  $isMobile,
  theme
}) => $isMobile ? mobileSpacing.base : theme.spacing.large);
const applyPaddingInline = () => css(["padding-inline-start:", "px;padding-inline-end:", "px;", ""], ({
  $paddingInline,
  theme
}) => getPadding(theme, $paddingInline), ({
  $paddingInline,
  theme
}) => getPadding(theme, $paddingInline), isMobile(css(["padding-inline-start:", "px;padding-inline-end:", "px;"], ({
  $paddingInline,
  theme
}) => getPadding(theme, $paddingInline, DEVICE_TYPES.MOBILE), ({
  $paddingInline,
  theme
}) => getPadding(theme, $paddingInline, DEVICE_TYPES.MOBILE))));
const Container = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30061128__sc-79445s-0"
})(["display:flex;flex-direction:column;flex-wrap:nowrap;justify-content:center;align-items:center;width:100%;box-sizing:border-box;", " padding-block-start:", "px;padding-block-end:", "px;margin-block-start:", "px;margin-block-end:", "px;", " background:", ";"], ({
  $paddingInline
}) => $paddingInline ? applyPaddingInline() : applyPaddingInlineDefault, ({
  $paddingBlockStart,
  theme
}) => getPadding(theme, $paddingBlockStart), ({
  $paddingBlockEnd,
  theme
}) => getPadding(theme, $paddingBlockEnd), ({
  $marginBlockStart,
  theme
}) => getPadding(theme, $marginBlockStart), ({
  $marginBlockEnd,
  theme
}) => getPadding(theme, $marginBlockEnd), isMobile(css(["padding-block-start:", "px;padding-block-end:", "px;margin-block-start:", "px;margin-block-end:", "px;"], ({
  $paddingBlockStart,
  theme
}) => getPadding(theme, $paddingBlockStart, DEVICE_TYPES.MOBILE), ({
  $paddingBlockEnd,
  theme
}) => getPadding(theme, $paddingBlockEnd, DEVICE_TYPES.MOBILE), ({
  $marginBlockStart,
  theme
}) => getPadding(theme, $marginBlockStart, DEVICE_TYPES.MOBILE), ({
  $marginBlockEnd,
  theme
}) => getPadding(theme, $marginBlockEnd, DEVICE_TYPES.MOBILE))), ({
  $bgColor
}) => getBgColor($bgColor));
const Content = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30061128__sc-79445s-1"
})(["display:flex;flex-direction:column;flex-wrap:nowrap;justify-content:center;align-content:stretch;align-items:center;width:100%;box-sizing:border-box;max-width:", ";"], ({
  $contentMaxWidth
}) => getContainerContentMaxWidth($contentMaxWidth));
const Section = ({
  children,
  paddingBlockStart,
  paddingBlockEnd,
  marginBlockStart,
  marginBlockEnd,
  bgColor,
  paddingInline,
  contentMaxWidth,
  ...rest
}) => {
  const isMobilePlatform = useIsMobile();
  return /*#__PURE__*/React__default.createElement(Container, _extends({}, rest, {
    $paddingBlockStart: paddingBlockStart,
    $paddingBlockEnd: paddingBlockEnd,
    $marginBlockStart: marginBlockStart,
    $marginBlockEnd: marginBlockEnd,
    $bgColor: bgColor,
    $paddingInline: paddingInline,
    $isMobile: isMobilePlatform
  }), /*#__PURE__*/React__default.createElement(Content, {
    $contentMaxWidth: contentMaxWidth
  }, children));
};
Section.propTypes = {
  /**
   * Content of the Section
   */
  children: PT.node.isRequired,
  /**
   * Logical block start padding (top padding)
   */
  paddingBlockStart: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  /**
   * Logical block end padding (bottom padding)
   */
  paddingBlockEnd: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  /**
   * Logical block start margin (top margin)
   */
  marginBlockStart: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  /**
   * Logical block end margin (bottom margin)
   */
  marginBlockEnd: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  /**
   * Background color
   */
  bgColor: PT.oneOf(['default', 'offset']),
  /**
   * Left and right padding around section content. Most sections should use `large`
   */
  paddingInline: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  /**
   * Max width of Section content
   */
  contentMaxWidth: PT.oneOf(['xsmall', 'small', 'base', 'large', 'fluid'])
};
Section.defaultProps = {
  paddingBlockStart: 'large',
  paddingBlockEnd: 'large',
  marginBlockStart: 'none',
  marginBlockEnd: 'none',
  bgColor: 'default',
  paddingInline: undefined,
  contentMaxWidth: 'fluid'
};

export { Section as S };
//# sourceMappingURL=Section-mnNV12Js.js.map
